import React, {createContext, useContext, useState, useEffect, useCallback} from 'react';

import Segment from './SegmentDto';
import SegmentState from './states';
import SegmentsList from './SegmentsListDto';
import SegmentListDto from './SegmentsListDto';

interface SegmentContextType {
  currentStates: SegmentState[];
  resetState: () => void;
  addState: (state: SegmentState) => void;
  removeState: (state: SegmentState) => void;

  activeSegmentId: Segment['id'] | null;
  setActiveSegment: (segmentId: Segment['id']) => void;

  segmentList: SegmentListDto;
  mergeAdjacentSegments: () => Promise<boolean>;
  addSegment: (segment?: Segment) => Promise<boolean>;
  removeSegment: (id: Segment['id']) => Promise<boolean>;
  splitSegment: (id: Segment['id'], splitPoint: number) => Promise<boolean>;
  updateSegment: (
    id: Segment['id'],
    newEnd: Segment['end'],
    newStart: Segment['start']
  ) => Promise<boolean>;
}

const initialState: SegmentContextType = {
  currentStates: [SegmentState.IDLE],
  addState: () => {},
  resetState: () => {},
  removeState: () => false,

  activeSegmentId: null,
  setActiveSegment: () => {},

  segmentList: new SegmentListDto(),
  addSegment: () => Promise.resolve(false),
  splitSegment: () => Promise.resolve(false),
  updateSegment: () => Promise.resolve(false),
  removeSegment: () => Promise.resolve(false),
  mergeAdjacentSegments: () => Promise.resolve(false),
};

const SegmentContext = createContext<SegmentContextType>(initialState);

export const SegmentProvider: React.FC = ({children}) => {
  const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
  const [segmentList, setSegmentList] = useState<SegmentListDto>(new SegmentListDto());
  const [currentStates, setCurrentStates] = useState<SegmentState[]>([SegmentState.IDLE]);

  const addState = useCallback((state: SegmentState) => {
    setCurrentStates(prevStates => {
      let newStates = prevStates;

      if (!prevStates.includes(state)) {
        newStates = [...prevStates, state];
      }

      return newStates;
    });
  }, []);

  const removeState = useCallback((state: SegmentState) => {
    setCurrentStates(prevStates => {
      let newStates = prevStates;

      if (prevStates.includes(state)) {
        newStates = prevStates.filter(prevState => prevState !== state);
      }

      return newStates;
    });
  }, []);

  const resetState = useCallback(() => {
    setCurrentStates([SegmentState.IDLE]);
  }, []);

  useEffect(
    function setIDLEStateIfNothingIsHappening() {
      if (!currentStates.length) {
        addState(SegmentState.IDLE);
      } else if (currentStates.length > 1) {
        removeState(SegmentState.IDLE);
      }
    },
    [currentStates, addState, removeState]
  );

  const setActiveSegment = useCallback((segmentId: Segment['id'] | null) => {
    setActiveSegmentId(segmentId);

    return true;
  }, []);

  const addSegment = useCallback(
    (externalSegment?: Segment) => {
      return new Promise<boolean>(resolve => {
        let segment = externalSegment;
        let missionAccomplished = false;

        if (!segment) {
          const lastSegment = segmentList.getLastSegment();
          const newSegmentStartPoint = lastSegment
            ? lastSegment.end + SegmentsList.INCREMENTS
            : SegmentsList.MIN_VALUE;

          segment = new Segment(newSegmentStartPoint, newSegmentStartPoint);
        }

        setSegmentList(prevList => {
          const newList = new SegmentListDto([...prevList.getAllSegments()]);

          missionAccomplished = newList.addSegment(segment as Segment);
          resolve(missionAccomplished);

          return newList;
        });
      });
    },
    [segmentList]
  );

  const removeSegment = useCallback((id: string) => {
    return new Promise<boolean>(resolve => {
      let missionAccomplished = false;

      setSegmentList(prevList => {
        const newList = new SegmentListDto([...prevList.getAllSegments()]);

        missionAccomplished = newList.removeSegment(id);
        resolve(missionAccomplished);

        return newList;
      });
    });
  }, []);

  const updateSegment = useCallback((id: string, newStart: number, newEnd: number) => {
    return new Promise<boolean>(resolve => {
      let missionAccomplished = false;

      setSegmentList(prevList => {
        const newList = new SegmentListDto([...prevList.getAllSegments()]);

        missionAccomplished = newList.updateSegment(id, newStart, newEnd);

        resolve(missionAccomplished);

        return newList;
      });
    });
  }, []);

  const splitSegment = useCallback((id: string, splitPoint: number) => {
    return new Promise<boolean>(resolve => {
      let missionAccomplished = false;

      setSegmentList(prevList => {
        const newList = new SegmentListDto([...prevList.getAllSegments()]);

        missionAccomplished = newList.splitSegment(id, splitPoint);
        resolve(missionAccomplished);

        return newList;
      });
    });
  }, []);

  const mergeAdjacentSegments = useCallback(() => {
    return new Promise<boolean>(resolve => {
      let missionAccomplished = false;

      setSegmentList(prevList => {
        let numbersOfMerges = 0;
        const newList = new SegmentListDto([...prevList.getAllSegments()]);

        numbersOfMerges = newList.mergeAdjacentSegments();
        missionAccomplished = numbersOfMerges > 0;
        resolve(missionAccomplished);

        return newList;
      });
    });
  }, []);

  return (
    <SegmentContext.Provider
      value={{
        currentStates,
        addState,
        resetState,
        removeState,

        segmentList,
        activeSegmentId,
        addSegment,
        splitSegment,
        removeSegment,
        updateSegment,
        setActiveSegment,
        mergeAdjacentSegments,
      }}
    >
      {children}
    </SegmentContext.Provider>
  );
};

export const useSegmentContext = () => useContext(SegmentContext);

export default SegmentContext;
